import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Format utilities for displaying data
 * These functions support internationalization via locale and currency parameters
 */

// Default locale settings (can be overridden per-call or globally via context)
let defaultLocale = 'en-US';
let defaultCurrency = 'USD';
let defaultDateFormat = 'MMM dd, yyyy';
let defaultTimeFormat = 'h:mm a';

/**
 * Set global default locale settings
 * Call this when tenant settings are loaded
 */
export function setLocaleDefaults(options: {
  locale?: string;
  currency?: string;
  dateFormat?: string;
  timeFormat?: string;
}) {
  if (options.locale) defaultLocale = options.locale;
  if (options.currency) defaultCurrency = options.currency;
  if (options.dateFormat) defaultDateFormat = options.dateFormat;
  if (options.timeFormat) defaultTimeFormat = options.timeFormat;
}

/**
 * Get current locale defaults
 */
export function getLocaleDefaults() {
  return {
    locale: defaultLocale,
    currency: defaultCurrency,
    dateFormat: defaultDateFormat,
    timeFormat: defaultTimeFormat,
  };
}

/**
 * Format date string to readable format
 * @param date - Date string or Date object
 * @param formatStr - date-fns format string (defaults to tenant preference)
 */
export function formatDate(date: string | Date, formatStr?: string): string {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, formatStr || defaultDateFormat);
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format date-only values in UTC (prevents timezone conversion issues)
 * Use this for dates that represent a calendar day without a specific time (e.g., installation dates)
 * @param date - Date string (ISO 8601) or Date object
 * @param formatStr - date-fns format string (defaults to tenant preference)
 */
export function formatDateUTC(date: string | Date, formatStr?: string): string {
  try {
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    return formatInTimeZone(dateStr, 'UTC', formatStr || defaultDateFormat);
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format date and time
 * @param date - Date string or Date object
 * @param dateFormatStr - date format (defaults to tenant preference)
 * @param timeFormatStr - time format (defaults to tenant preference)
 */
export function formatDateTime(
  date: string | Date,
  dateFormatStr?: string,
  timeFormatStr?: string
): string {
  const df = dateFormatStr || defaultDateFormat;
  const tf = timeFormatStr || defaultTimeFormat;
  return formatDate(date, `${df} ${tf}`);
}

/**
 * Format phone number based on country
 * @param phone - Phone number string
 * @param country - ISO 3166-1 alpha-2 country code (defaults to 'US')
 */
export function formatPhoneNumber(phone: string, country: string = 'US'): string {
  const cleaned = phone.replace(/\D/g, '');

  switch (country) {
    case 'US':
    case 'CA':
      // North American format: (XXX) XXX-XXXX
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      if (cleaned.length === 11 && cleaned.startsWith('1')) {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      }
      break;
    case 'AU':
      // Australian format: 04XX XXX XXX (mobile) or (0X) XXXX XXXX (landline)
      if (cleaned.length === 10 && cleaned.startsWith('04')) {
        return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
      }
      if (cleaned.length === 10 && cleaned.startsWith('0')) {
        return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)} ${cleaned.slice(6)}`;
      }
      break;
    case 'GB':
      // UK format: varies by area code
      if (cleaned.length === 11 && cleaned.startsWith('0')) {
        return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
      }
      break;
  }

  // Fallback: return as-is or with minimal formatting
  return phone;
}

/**
 * Format currency
 * @param amount - Numeric amount
 * @param currency - ISO 4217 currency code (defaults to tenant preference)
 * @param locale - BCP 47 locale tag (defaults to tenant preference)
 */
export function formatCurrency(
  amount: number,
  currency?: string,
  locale?: string
): string {
  return new Intl.NumberFormat(locale || defaultLocale, {
    style: 'currency',
    currency: currency || defaultCurrency,
  }).format(amount);
}

/**
 * Format number with locale-appropriate separators
 * @param num - Number to format
 * @param locale - BCP 47 locale tag (defaults to tenant preference)
 */
export function formatNumber(num: number, locale?: string): string {
  return new Intl.NumberFormat(locale || defaultLocale).format(num);
}

/**
 * Format internet speed
 */
export function formatSpeed(mbps: number): string {
  return `${mbps} Mbps`;
}

/**
 * Format percentage
 */
export function formatPercent(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Get relative time (e.g., "2 hours ago")
 */
export function getRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

  return formatDate(dateObj);
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Format status badge text
 */
export function formatStatus(status: string): string {
  return status.replace(/([A-Z])/g, ' $1').trim();
}
